import React, { useContext, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { smartAssign } from "../../service/Task";
import { smartAssignRulesList, assignByArea } from "../../service/Configuration";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import { Spinner } from "../../Spinner";
import { IoSparkles, IoList, IoSearch, IoChevronDown } from "react-icons/io5";

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

const SmartAssignModal = ({ isOpen, onClose }) => {
  const { projectId, activityId, langMode, theme } = useContext(AppContext);
  const isToggleRtl = langMode === "ar" || langMode === "ur";
  const [isreassignChecked, setIsReassignChecked] = useState(false);
  const [showAssignByRules, setShowAssignByRules] = useState(false);
  const [assignmentList, setAssignmentList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [assignRuleloading, setAssignRuleloading] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(4);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef(null);
  const sentinelRef = useRef(null);


  const runSmartAssign = async () => {
    if (!projectId) {
      toast.warn(translations[langMode]?.invalidworkflow ?? "Select project/activity first.");
      return;
    }
    setIsLoading(true);
    try {
      // activityId can be a comma-joined list when set from other screens
      // (e.g. Configuration's multi-select); only scope to a single workflow.
      const workflowId = /^\d+$/.test(String(activityId ?? "")) ? activityId : undefined;
      const responseData = await smartAssign(projectId, isreassignChecked, workflowId);
      if (responseData?.header?.code === 600) {
        toast.success(responseData?.body?.value ?? "Smart assign completed.");
        setIsReassignChecked(false);
        onClose()
      } else {
        const backendMessage =
          responseData?.body?.value ??
          responseData?.body?.error ??
          responseData?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Error during smart assign:", error);
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignmentRules = async (reset = false, pageNum = 0) => {
    if (!projectId) return;
    // reset = initial load (shows list loader); otherwise a silent scroll load.
    const setLoading = reset ? setRulesLoading : setLoadingMore;
    setLoading(true);
    try {
      await smartAssignRulesList(
        reset,
        projectId,
        pageNum,
        size,
        setLoading,
        setAssignmentList,
        setHasMore,
        () => { }
      );
    } catch (error) {
      console.error("Error fetching assignment rules:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignByRules = async (selectedIds) => {
    if (!selectedIds?.length) return;
    setAssignRuleloading(true);
    try {
      const response = await assignByArea(selectedIds, isreassignChecked, activityId);
      if (response?.header?.code === 600) {
        toast.success(response?.body?.value ?? "Assign by rules completed.");
        setSelectedIds([]);
        setIsReassignChecked(false);
        onClose?.();
      } else {
        throw new Error(response?.header?.message);
      }
    } catch (error) {
      console.error("Error assigning by area:", error);
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setAssignRuleloading(false);
    }
  };

  // Initial load when the rules section opens.
  useEffect(() => {
    if (showAssignByRules && projectId) {
      fetchAssignmentRules(true, 0);
    }
  }, [showAssignByRules, projectId]);

  // Load subsequent pages as the user scrolls (silent, no list re-mount).
  useEffect(() => {
    if (showAssignByRules && hasMore && !rulesLoading && !loadingMore && page > 0) {
      fetchAssignmentRules(false, page);
    }
  }, [page]);

  // Trigger the next page when the bottom sentinel scrolls into view.
  useEffect(() => {
    if (!showAssignByRules || !hasMore || rulesLoading || loadingMore) return;
    const rootEl = listRef.current;
    const sentinelEl = sentinelRef.current;
    if (!rootEl || !sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setPage((p) => p + 1);
        }
      },
      { root: rootEl, threshold: 0.1 }
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [showAssignByRules, hasMore, rulesLoading, loadingMore, assignmentList.length]);

  const toggleSelected = (id) => {
    if (id == null) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Client-side filtering over the rules already loaded via infinite scroll.
  const filteredAssignments = assignmentList.filter((assignment) => {
    const name = assignment?.ruleName ?? assignment?.rule_name ?? "";
    return name.toLowerCase().includes(searchQuery.trim().toLowerCase());
  });
  const filteredIds = filteredAssignments
    .map((assignment) => assignment?.id)
    .filter((id) => id != null);
  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const toggleRulesSection = () => {
    setShowAssignByRules((prev) => {
      if (!prev) {
        setPage(0);
        setAssignmentList([]);
        setSelectedIds([]);
        setSearchQuery("");
        setHasMore(true);
      }
      return !prev;
    });
  };

  const handleClose = () => {
    setShowAssignByRules(false);
    setAssignmentList([]);
    setSelectedIds([]);
    setSearchQuery("");
    setPage(0);
    onClose?.();
  };

  if (!isOpen) return null;

  const isDark = theme === "dark";
  const isSmartAssignDisabled = isLoading || !projectId;

  const modalContent = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="smart-assign-title"
        className={`relative w-full max-w-lg rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh]
          ${isDark ? "bg-gray-800 border border-gray-700" : "bg-white"}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b
          ${isDark ? "border-gray-700" : "border-gray-100"}
        `}>
          <h2
            id="smart-assign-title"
            className={`min-w-0 text-base font-semibold truncate ${isDark ? "text-white" : "text-gray-800"}`}
          >
            {translations[langMode]?.smartAssign ?? "Smart Assign"}
          </h2>
          <ModalCloseButton onClick={handleClose} ariaLabel="Close" />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 space-y-4">
          {/* Reassign toggle */}
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-3
            ${isDark ? "bg-gray-700/40 border-gray-700" : "bg-gray-50 border-gray-100"}
          `}>
            <span className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
              {translations[langMode]?.reassign ?? "Reassign"}
            </span>
            <div className="select-none caret-transparent">
              <input
                id="smart-assign-reassign-toggle"
                type="checkbox"
                role="switch"
                aria-checked={isreassignChecked}
                checked={isreassignChecked}
                onChange={() => setIsReassignChecked((v) => !v)}
                className="hidden"
              />
              <label
                htmlFor="smart-assign-reassign-toggle"
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out select-none caret-transparent ${
                  isreassignChecked ? "bg-[#236DB4]" : isDark ? "bg-gray-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                    isreassignChecked
                      ? isToggleRtl
                        ? "-translate-x-6"
                        : "translate-x-6"
                      : isToggleRtl
                        ? "-translate-x-1"
                        : "translate-x-1"
                  }`}
                />
              </label>
            </div>
          </div>

          {/* Action cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={runSmartAssign}
              disabled={isSmartAssignDisabled}
              className={`group flex items-center gap-3 rounded-xl border p-4 text-start transition-all duration-200
                ${isDark
                  ? "bg-gray-700/40 border-gray-700 hover:border-[#236DB4]"
                  : "bg-white border-gray-200 hover:border-[#236DB4] hover:shadow-md"}
                ${isSmartAssignDisabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}
              `}
            >
              {isLoading ? (
                <Spinner />
              ) : (
                <>
                  <span className={`flex items-center justify-center h-10 w-10 shrink-0 rounded-lg transition-transform group-hover:scale-105
                    ${isDark ? "bg-[#236DB4]/30 text-blue-300" : "bg-[#236DB4]/10 text-[#236DB4]"}
                  `}>
                    <IoSparkles size={20} />
                  </span>
                  <span className={`text-sm font-semibold ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                    {translations[langMode]?.smartAssign ?? "Smart Assign"}
                  </span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={toggleRulesSection}
              aria-expanded={showAssignByRules}
              className={`group flex items-center gap-3 rounded-xl border p-4 text-start transition-all duration-200 cursor-pointer
                ${showAssignByRules
                  ? isDark
                    ? "bg-[#236DB4]/20 border-[#236DB4]"
                    : "bg-[#236DB4]/5 border-[#236DB4] shadow-sm"
                  : isDark
                    ? "bg-gray-700/40 border-gray-700 hover:border-[#236DB4]"
                    : "bg-white border-gray-200 hover:border-[#236DB4] hover:shadow-md"}
              `}
            >
              <span className={`flex items-center justify-center h-10 w-10 shrink-0 rounded-lg transition-transform group-hover:scale-105
                ${isDark ? "bg-slate-600 text-slate-200" : "bg-slate-100 text-slate-600"}
              `}>
                <IoList size={20} />
              </span>
              <span className={`flex-1 text-sm font-semibold ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                {translations[langMode]?.assignbyrule ?? "Assign by Rules"}
              </span>
              <IoChevronDown
                size={16}
                className={`shrink-0 text-gray-400 transition-transform duration-200 ${showAssignByRules ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {/* Rules list (expandable) */}
          {showAssignByRules && (
            <div className={`rounded-xl border p-3 sm:p-4 space-y-3
              ${isDark ? "bg-gray-700/40 border-gray-700" : "bg-gray-50 border-gray-100"}
            `}>
              {/* Search + Select All */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <IoSearch
                    size={16}
                    className={`absolute top-1/2 -translate-y-1/2 text-gray-400 ${isToggleRtl ? "right-3" : "left-3"}`}
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={translations[langMode]?.search ?? "Search rules..."}
                    className={`w-full h-9 ${isToggleRtl ? "pr-9 pl-3" : "pl-9 pr-3"} text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#236DB4]/30 focus:border-[#236DB4]
                      ${isDark
                        ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400"
                        : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}
                    `}
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  disabled={filteredIds.length === 0}
                  className={`h-9 whitespace-nowrap text-sm font-medium px-3 rounded-lg border transition
                    ${filteredIds.length === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    ${isDark
                      ? "bg-gray-700 border-gray-600 text-blue-300 hover:bg-gray-600"
                      : "bg-white border-[#236DB4]/30 text-[#236DB4] hover:bg-[#236DB4]/5"}
                  `}
                >
                  {allFilteredSelected
                    ? translations[langMode]?.deselectAll ?? "Deselect All"
                    : translations[langMode]?.selectAll ?? "Select All"}
                </button>
              </div>
              <div
                ref={listRef}
                className={`max-h-48 overflow-y-auto app-scrollbar rounded-lg border
                  ${isDark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}
                `}
              >
                <ul className="p-1.5 space-y-0.5">
                  {filteredAssignments.map((assignment, index) => {
                    const id = assignment?.id ?? index;
                    const checked = selectedIds.includes(assignment?.id);
                    return (
                      <li
                        key={id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition
                          ${checked
                            ? isDark ? "bg-[#236DB4]/20" : "bg-[#236DB4]/5"
                            : isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"}
                        `}
                      >
                        <input
                          id={`rule-${id}`}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelected(assignment?.id)}
                          className="h-4 w-4 shrink-0 rounded border-gray-300 cursor-pointer accent-[#236DB4]"
                        />
                        <label
                          htmlFor={`rule-${id}`}
                          className={`flex-1 min-w-0 truncate text-sm cursor-pointer ${isDark ? "text-gray-200" : "text-gray-800"}`}
                        >
                          {assignment?.ruleName ?? assignment?.rule_name ?? `Rule ${index + 1}`}
                        </label>
                      </li>
                    );
                  })}
                  {rulesLoading && assignmentList.length === 0 && (
                    <li className="py-3 text-center text-sm text-gray-500">{translations[langMode]?.Loading ?? "Loading..."}</li>
                  )}
                  {!rulesLoading && !loadingMore && assignmentList.length === 0 && (
                    <li className="py-4 text-center text-sm text-gray-500">{translations[langMode]?.norule ?? "No rules found."}</li>
                  )}
                  {!rulesLoading && assignmentList.length > 0 && filteredAssignments.length === 0 && (
                    <li className="py-4 text-center text-sm text-gray-500">{translations[langMode]?.norule ?? "No rules found."}</li>
                  )}
                  {loadingMore && (
                    <li className="py-2 text-center text-xs text-gray-400">{translations[langMode]?.Loading ?? "Loading..."}</li>
                  )}
                </ul>
                {/* Sentinel: when this scrolls into view, the next page loads */}
                {hasMore && assignmentList.length > 0 && (
                  <div ref={sentinelRef} className="h-1" aria-hidden="true" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer (Assign by Rules) */}
        {showAssignByRules && (
          <div className={`flex items-center justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t
            ${isDark ? "border-gray-700" : "border-gray-100"}
          `}>
            <CommonButton colorIntent="danger"
              onClick={handleClose}
              variant="danger"
              tooltipPosition="top"
              wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
              className="w-full !h-10 !rounded-lg justify-center"
            >
              {translations[langMode]?.cancel ?? "Cancel"}
            </CommonButton>
            <CommonButton colorIntent="success"
              variant="primary"
              onClick={() => handleAssignByRules(selectedIds)}
              disabled={assignRuleloading || selectedIds.length === 0}
              tooltipPosition="top"
              wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
              className="w-full !h-10 !rounded-lg justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assignRuleloading ? (translations[langMode].Assigning) : (translations[langMode]?.assign ?? "Assign")}
            </CommonButton>
          </div>
        )}
      </div>
      {assignRuleloading && (
        <Spinner/>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SmartAssignModal;
